import Check from '@mui/icons-material/Check'
import Videocam from '@mui/icons-material/Videocam'
import { useNavigate, useParams } from 'react-router-dom'
import { useTheme } from '@mui/styles'
import { useRef } from 'react'
import { useHoverDirty } from 'react-use'
import { GridChildComponentProps } from 'react-window'
import { BucketMedia } from '../../../../graphql/generated'
import useSelectedMedia from '../../hooks/useSelectedMedia'
import formatImageProxyUrl from '../../utils/formatImageProxyUrl'
import { imagePlaceholder, videoPlaceholder } from '../../utils/placeholders'
import CustomIconButton from './CustomIconButton'

type IconSize = 'inherit' | 'large' | 'medium' | 'small'

// Include numCols with the component props, because we're not using zoom via
// settings here; it's actually coming from hard-coded numbers in the grid
// component, since the user may not have any stored settings
interface GridChildComponentPropsWithColNums<T> extends GridChildComponentProps {
  numCols: number
  data: T
}

export default function DesktopCell({ data, rowIndex, columnIndex, style, numCols }: Readonly<GridChildComponentPropsWithColNums<BucketMedia[]>>) {
  const { shareId, albumId } = useParams<{ shareId: string; albumId: string }>()
  const theme = useTheme()
  const { selectedMedia, toggleSelectMedia, selectedModeEnabled } = useSelectedMedia()
  const ref = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()
  const isHovering = useHoverDirty(ref)
  const index = rowIndex * numCols + columnIndex
  const item = data[index]
  const src = formatImageProxyUrl(item, Number(style.width))
  const isSelected = item && !!selectedMedia.get(item.id)
  let iconSize: IconSize = 'small'

  if (numCols < 4) iconSize = 'medium'

  const handleError = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
    e.currentTarget.src = item?.mediaType?.includes('video') ? videoPlaceholder : imagePlaceholder
    e.currentTarget.srcset = item?.mediaType?.includes('video') ? videoPlaceholder : imagePlaceholder
  }

  const handleClick = () => {
    navigate(`media/${item.id}`, {
      state: {
        returnUri: albumId ? `/share/${shareId}/album/${albumId}` : `/share/${shareId}`
      }
    })
  }

  const handleToggle = () => {
    if (!item) return

    // TODO: Fix and re-add keyboard shortcut for shift key to bulk select

    toggleSelectMedia(item.id)
  }

  if (!item) {
    return null
  }

  return (
    <div style={{ ...style, top: Number(style.top) + 60 }} data-test={`cell-${index}`} ref={ref}>
      <CustomIconButton
        isActive={selectedModeEnabled || isSelected || isHovering}
        onClick={handleToggle}
        sx={{
          top: 8,
          left: 8,
          zIndex: 1,
          backgroundColor: isSelected ? theme.palette.primary.main : 'rgba(71,85,105,0.5)',
          '&:hover': { backgroundColor: isSelected ? 'white' : 'rgba(71,85,105,0.9)' }
        }}
        data-test={isSelected ? 'deselect' : 'select'}
      >
        {isSelected && (
          <Check color="inherit" fontSize={iconSize} sx={{ background: theme.palette.primary.main, borderRadius: '50%', padding: '2px' }} />
        )}
        {!isSelected && (
          <Check color="inherit" fontSize={iconSize} sx={{ background: 'transparent', color: 'white', borderRadius: '50%', padding: '2px' }} />
        )}
      </CustomIconButton>
      {item?.mediaType?.includes('video') && (
        <Videocam
          sx={{
            color: 'white',
            opacity: '0.8',
            position: 'absolute',
            top: 8,
            right: 12,
            [theme.breakpoints.down('sm')]: {
              fontSize: 'large'
            }
          }}
        />
      )}
      <img
        alt={item.id}
        role="presentation"
        src={src}
        srcSet={src}
        onClick={handleClick}
        onError={handleError}
        style={{
          borderStyle: 'solid',
          borderWidth: 3,
          borderColor: 'transparent',
          width: style.width,
          height: Number(style.height) - 32,
          objectFit: 'cover',
          display: 'flex',
          userSelect: 'none',
          WebkitUserSelect: 'none'
        }}
      />
    </div>
  )
}
